import React, { useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useOnboardingSession } from '../../hooks/useOnboardingSession';
import OnboardingPathPage from '../../pages/OnboardingPathPage';
import type { OnboardingSessionResponse } from '@codemerlin/shared/types/onboarding';
import type { OnboardingPath } from '@codemerlin/shared/constants/onboarding';
import {
  ONBOARDING_RECHOOSE_QUERY,
  ONBOARDING_RECHOOSE_VALUE,
} from '../../features/onboarding/constants/navigation';

const SCMConnectionPage = lazy(() => import('../../pages/onboarding/SCMConnectionPage'));
const CompanyNameStep = lazy(() => import('../../features/onboarding/steps/CompanyNameStep'));
const RepositorySelectionStep = lazy(
  () => import('../../features/onboarding/steps/RepositorySelectionStep')
);
const WorkItemStep = lazy(() => import('../../features/onboarding/steps/WorkItemStep'));
const ReviewContextStep = lazy(() => import('../../features/onboarding/steps/ReviewContextStep'));
const McpConfigurationStep = lazy(
  () => import('../../features/onboarding/steps/McpConfigurationStep')
);
const LegacyOnboardingStepRedirect = lazy(
  () => import('../../features/onboarding/components/LegacyOnboardingStepRedirect')
);

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { session, isLoading, updateSession } = useOnboardingSession();

  const allowPathReselection =
    searchParams.get(ONBOARDING_RECHOOSE_QUERY) === ONBOARDING_RECHOOSE_VALUE;

  // Redirect to next incomplete step if user lands on /onboarding
  // but has already started a session (unless re-selecting path via ?rechoose=1)
  useEffect(() => {
    if (isLoading || !session || location.pathname !== '/onboarding') {
      return;
    }
    // Setup is owner-only; invited members belong in the workspace (DC-479).
    if (session.quickstartRestricted === true) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (session.status === 'completed') {
      navigate('/');
      return;
    }
    if (session.nextUrl) {
      if (allowPathReselection) {
        return;
      }
      navigate(session.nextUrl);
    }
  }, [session, isLoading, navigate, location.pathname, allowPathReselection]);

  const handleSelect = (path: string) => {
    updateSession(
      { path: path as OnboardingPath },
      {
        onSuccess: (data: OnboardingSessionResponse) => {
          if (data.nextUrl) {
            navigate(data.nextUrl);
          }
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your onboarding session...</p>
        </div>
      </div>
    );
  }

  return <OnboardingPathPage onSelect={handleSelect} />;
};

/**
 * Higher-order component to protect onboarding routes
 */
export const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading } = useOnboardingSession();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading) {
      // If no session exists, redirect to path selection
      if (!session) {
        navigate('/onboarding');
        return;
      }

      // Non-owner members never get setup steps, even via a direct step URL (DC-479)
      if (session.quickstartRestricted === true) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // If session is completed, redirect to dashboard
      if (session.status === 'completed' && !location.search.includes('onboarding=complete')) {
        navigate('/');
      }
    }
  }, [session, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Hold back the step UI rather than flashing setup content the member cannot use.
  if (session?.quickstartRestricted === true) {
    return null;
  }

  return <>{children}</>;
};

export const onboardingRoutes = [
  {
    path: 'onboarding',
    element: <OnboardingPage />,
  },
  {
    path: 'onboarding/quick/company',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <CompanyNameStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/company',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <CompanyNameStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/quick/scm',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <SCMConnectionPage />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/scm',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <SCMConnectionPage />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/features',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <LegacyOnboardingStepRedirect />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/workitems',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <WorkItemStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/policies',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <LegacyOnboardingStepRedirect />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/standards',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <ReviewContextStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/mcp',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <McpConfigurationStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/reporting',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <LegacyOnboardingStepRedirect />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/quick/repos',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <RepositorySelectionStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/repos',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <RepositorySelectionStep />
        </Suspense>
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/full/summary',
    element: (
      <OnboardingRoute>
        <Suspense fallback={<Loader2 className="h-8 w-8 animate-spin" />}>
          <LegacyOnboardingStepRedirect />
        </Suspense>
      </OnboardingRoute>
    ),
  },
];
